import React, { useEffect, useState, useRef } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { fmtINR } from "../../lib/constants";
import Paginated from "../../components/Paginated";
import ImageUploader from "../../components/ImageUploader";
import { Plus, X, Upload, Package, Tag, RefreshCcw, BadgeIndianRupee, Barcode } from "lucide-react";

const OCCASIONS = ["Wedding", "Birthday", "Gifting", "Reception", "Festive", "Daily wear"];

export default function InventoryPage() {
  const [tab, setTab] = useState("stock");
  return (
    <div className="p-6 lg:p-8">
      <div className="mb-6">
        <div className="eyebrow">Operations</div>
        <h1 className="font-display text-4xl text-maroon">Live Inventory</h1>
        <p className="text-xs text-ash mt-1">Upload the label report from the offline software; each piece (LabelNo) is tracked live by category.</p>
      </div>
      <div className="flex gap-1 border-b border-line mb-6 flex-wrap">
        {[["stock", "Stock", Package], ["pieces", "Pieces", Barcode], ["categories", "Items", Tag], ["upload", "Upload", Upload], ["credits", "Store Credit", BadgeIndianRupee]].map(([t, label, Icon]) => (
          <button key={t} onClick={() => setTab(t)} className={`px-4 py-2 text-sm inline-flex items-center gap-1 ${tab === t ? "border-b-2 border-maroon text-maroon font-semibold" : "text-ash"}`} data-testid={`inv-tab-${t}`}>
            <Icon size={14}/> {label}
          </button>
        ))}
      </div>
      {tab === "stock" && <StockTab />}
      {tab === "pieces" && <PiecesTab />}
      {tab === "categories" && <CategoriesTab />}
      {tab === "upload" && <UploadTab />}
      {tab === "credits" && <CreditsTab />}
    </div>
  );
}

// ---------------- Stock overview ----------------
function StockTab() {
  const [rows, setRows] = useState([]);
  const [syncing, setSyncing] = useState(false);
  const load = () => api.get("/api/crm/inventory/stock").then((r) => setRows(r.data || [])).catch(() => {});
  useEffect(() => { load(); }, []);
  const badge = (s) => s.state === "sold_out" ? "bg-red-100 text-red-700" : s.state === "low" ? "bg-amber-100 text-amber-800" : s.state === "made_to_order" ? "bg-gold/20 text-maroon" : s.state === "manual" ? "bg-blue-50 text-blue-700" : "bg-green-100 text-green-700";
  const syncStorefront = async () => {
    setSyncing(true);
    try { const { data } = await api.post("/api/crm/inventory/sync-storefront"); toast.success(`Synced ${data.synced} designs to the storefront`); }
    catch (e) { toast.error(e?.response?.data?.detail || "Sync failed"); } finally { setSyncing(false); }
  };
  return (
    <div>
      <div className="flex items-center gap-2 mb-3">
        <button onClick={load} className="btn-outline text-xs inline-flex items-center gap-1"><RefreshCcw size={12}/> Refresh</button>
        <button onClick={syncStorefront} disabled={syncing} className="btn-maroon text-xs" data-testid="sync-storefront">{syncing ? "Syncing…" : "Sync to storefront"}</button>
        <span className="text-[11px] text-ash">Bulk-CSV designs appear on the website automatically; use this to re-mirror if needed.</span>
      </div>
      <Paginated items={rows} pageSize={15} label="lines" scroller>
        {(page) => (
          <div className="bg-white border border-line overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-pearl border-b border-line"><tr>{["Item", "Category (design)", "Source", "Available", "Reserved", "Sold", "Total", "Status"].map(h => <th key={h} className="text-left p-3 eyebrow text-[10px]">{h}</th>)}</tr></thead>
              <tbody>
                {page.map((r, i) => (
                  <tr key={i} className="border-b border-line/60" data-testid={`stock-${r.category}`}>
                    <td className="p-3 text-xs text-ash">{r.item_name}</td>
                    <td className="p-3 font-medium">{r.category || <span className="text-ash italic">— uncategorised —</span>}</td>
                    <td className="p-3">
                      <span className={`text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded ${r.source === "manual" ? "bg-blue-50 text-blue-700" : "bg-gold/20 text-maroon"}`}>
                        {r.source === "manual" ? "Manual" : "Bulk CSV"}
                      </span>
                    </td>
                    <td className="p-3 font-semibold text-maroon">{r.available ?? "—"}</td>
                    <td className="p-3 text-xs">{r.reserved}</td>
                    <td className="p-3 text-xs text-ash">{r.sold}</td>
                    <td className="p-3 text-xs">{r.total ?? "—"}</td>
                    <td className="p-3"><span className={`text-[10px] px-2 py-0.5 rounded ${badge(r)}`}>{r.message}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
            {rows.length === 0 && <div className="p-12 text-center text-ash text-sm">No stock yet — upload a label report.</div>}
          </div>
        )}
      </Paginated>
    </div>
  );
}

// ---------------- Pieces (one row per physical item) ----------------
// The bulk upload puts ten peacock chains at ten different weights into stock.
// This view lists them as ten rows — each with its own barcode, grams and
// price — plus every column the uploaded sheet carried, so the ledger reads the
// same as the Excel file it came from.
function PiecesTab() {
  const [data, setData] = useState({ rows: [], sheet_columns: [] });
  const [cats, setCats] = useState([]);
  const [category, setCategory] = useState("");
  const [status, setStatus] = useState("");
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [showSheet, setShowSheet] = useState(false);

  const load = () => {
    setLoading(true);
    const params = new URLSearchParams();
    if (category) params.set("category", category);
    if (status) params.set("status", status);
    if (q) params.set("q", q);
    api.get(`/api/crm/inventory/pieces?${params}`)
      .then((r) => setData(r.data || { rows: [], sheet_columns: [] }))
      .catch(() => setData({ rows: [], sheet_columns: [] }))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [category, status]);
  useEffect(() => { api.get("/api/crm/inventory/categories").then((r) => setCats(r.data || [])).catch(() => {}); }, []);

  const rows = data.rows || [];
  const sheetCols = data.sheet_columns || [];
  const available = rows.filter((r) => r.status === "available").length;
  const totalGrams = rows.reduce((a, r) => a + (Number(r.net_wt) || 0), 0);

  const badge = (st) => st === "available" ? "bg-green-100 text-green-700"
    : st === "reserved" ? "bg-amber-100 text-amber-800"
    : st === "sold" ? "bg-red-100 text-red-700" : "bg-blue-50 text-blue-700";

  const exportCsv = () => {
    const base = ["Barcode", "Item", "Category", "Metal", "Purity", "Gross Wt", "Net Wt",
                  "Size", "Wastage %", "Labour", "Other", "HUID Charges", "HUID", "Price", "Status"];
    const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const csv = [base.concat(sheetCols).join(",")].concat(rows.map((r) => [
      r.label_no, r.item_name, r.category, r.metal, r.purity, r.gross_wt, r.net_wt, r.size,
      r.wastage_pct, r.labour_charges, r.other_charges, r.huid_charges, r.huid, r.price, r.status,
    ].concat(sheetCols.map((c) => (r.columns || {})[c] ?? "")).map(esc).join(","))).join("\n");
    const a = document.createElement("a");
    a.href = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    a.download = "inventory-pieces.csv"; a.click();
    setTimeout(() => URL.revokeObjectURL(a.href), 5000);
  };

  return (
    <div>
      <div className="grid sm:grid-cols-3 gap-4 mb-4">
        <div className="bg-white border border-line p-4"><div className="eyebrow text-[10px]">Pieces listed</div><div className="font-display text-2xl text-maroon">{rows.length}</div></div>
        <div className="bg-white border border-line p-4"><div className="eyebrow text-[10px]">Available</div><div className="font-display text-2xl text-maroon">{available}</div></div>
        <div className="bg-white border border-line p-4"><div className="eyebrow text-[10px]">Total weight</div><div className="font-display text-2xl text-maroon">{totalGrams.toFixed(3)} g</div></div>
      </div>

      <div className="flex flex-wrap gap-2 mb-3">
        <select className="border border-line px-3 py-2 bg-white text-sm" value={category} onChange={(e) => setCategory(e.target.value)} data-testid="pieces-category">
          <option value="">All designs</option>
          {cats.map((c) => <option key={c.id} value={c.name}>{c.name}</option>)}
        </select>
        <select className="border border-line px-3 py-2 bg-white text-sm" value={status} onChange={(e) => setStatus(e.target.value)} data-testid="pieces-status">
          <option value="">All statuses</option>
          <option value="available">Available</option>
          <option value="reserved">Reserved</option>
          <option value="sold">Sold</option>
        </select>
        <input className="border border-line px-3 py-2 bg-white text-sm w-full sm:w-72" placeholder="Search barcode, design, HUID…" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()} data-testid="pieces-search"/>
        <button onClick={load} className="btn-outline text-sm">Search</button>
        {sheetCols.length > 0 && (
          <button onClick={() => setShowSheet((v) => !v)} className="btn-outline text-sm" data-testid="pieces-sheet-toggle">
            {showSheet ? "Hide" : "Show"} sheet columns ({sheetCols.length})
          </button>
        )}
        <button onClick={exportCsv} className="btn-outline text-sm">Export CSV</button>
      </div>

      {loading ? <div className="text-center text-ash py-16">Loading…</div> : (
        <Paginated items={rows} pageSize={25} label="pieces" scroller>
          {(page) => (
            <div className="bg-white border border-line overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-pearl border-b border-line">
                  <tr>
                    {["Barcode", "Design", "Metal", "Purity", "Gross", "Net Wt", "Size",
                      "Wastage", "Labour (not charged)", "HUID Chg", "HUID", "Price", "In Stock", "Status"]
                      .map((h) => <th key={h} className="text-left p-3 eyebrow text-[10px] whitespace-nowrap">{h}</th>)}
                    {showSheet && sheetCols.map((c) => (
                      <th key={c} className="text-left p-3 eyebrow text-[10px] whitespace-nowrap bg-gold/10">{c}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {page.map((r) => (
                    <tr key={r.id || r.label_no} className="border-b border-line/60" data-testid={`piece-${r.label_no}`}>
                      <td className="p-3 font-mono text-xs text-maroon whitespace-nowrap">{r.label_no}</td>
                      <td className="p-3"><div className="font-medium">{r.category}</div><div className="text-[10px] text-ash">{r.item_name}</div></td>
                      <td className="p-3 text-xs">{r.metal || "Gold"}</td>
                      <td className="p-3 text-xs">{r.purity}</td>
                      <td className="p-3 text-xs">{r.gross_wt || "—"}</td>
                      <td className="p-3 text-xs font-medium">{r.net_wt} g</td>
                      <td className="p-3 text-xs">{r.size || "—"}</td>
                      <td className="p-3 text-xs">{r.wastage_pct ? `${r.wastage_pct}%` : "—"}</td>
                      <td className="p-3 text-xs">{r.labour_charges ? fmtINR(r.labour_charges) : "—"}</td>
                      {/* Rs 1250 unless the sheet gave this piece its own figure. */}
                      <td className="p-3 text-xs">{r.huid_charges ? fmtINR(r.huid_charges) : "—"}</td>
                      <td className="p-3 text-[10px] font-mono">{r.huid || "—"}</td>
                      <td className="p-3 text-maroon font-semibold whitespace-nowrap">{fmtINR(r.price)}</td>
                      {/* Every piece is a single item — one row, one unit. */}
                      <td className="p-3 text-xs font-semibold">{r.available}</td>
                      <td className="p-3"><span className={`text-[10px] px-2 py-0.5 rounded capitalize ${badge(r.status)}`}>{r.status}</span></td>
                      {showSheet && sheetCols.map((c) => (
                        <td key={c} className="p-3 text-[11px] text-ash whitespace-nowrap bg-gold/5">{(r.columns || {})[c] || "—"}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
              {rows.length === 0 && <div className="p-12 text-center text-ash text-sm">No pieces match this filter — upload a label report to add stock.</div>}
            </div>
          )}
        </Paginated>
      )}
    </div>
  );
}

// ---------------- Categories ----------------
const EMPTY_CAT = { name: "", item_name: "", metal: "Gold", parent_category: "", occasions: [], images: [], description: "", gender: "", made_to_order: false, active: true, same_image_for_all: true };
function CategoriesTab() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(EMPTY_CAT);
  const [editing, setEditing] = useState(null);
  const load = () => api.get("/api/crm/inventory/categories").then((r) => setItems(r.data || [])).catch(() => {});
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!form.name.trim()) { toast.error("Category name is required (must match the file's Category value)"); return; }
    try {
      if (editing) await api.patch(`/api/crm/inventory/categories/${editing}`, form);
      else await api.post("/api/crm/inventory/categories", form);
      toast.success(editing ? "Category updated" : "Category added");
      setForm(EMPTY_CAT); setEditing(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const edit = (c) => { setEditing(c.id); setForm({ ...EMPTY_CAT, ...c }); window.scrollTo({ top: 0, behavior: "smooth" }); };
  const del = async (id) => { if (!window.confirm("Delete this category?")) return; await api.delete(`/api/crm/inventory/categories/${id}`); load(); };
  const toggleOcc = (o) => setForm((f) => ({ ...f, occasions: f.occasions.includes(o) ? f.occasions.filter((x) => x !== o) : [...f.occasions, o] }));

  return (
    <div className="grid lg:grid-cols-2 gap-6">
      <div className="bg-white border border-line p-5">
        <div className="eyebrow mb-3">{editing ? "Edit category" : "Add category"}</div>
        <p className="text-[11px] text-ash mb-3">The <b>name</b> must exactly match the <b>Category</b> value in the upload (e.g. "FANCY BALI"). Set which occasions it belongs to so it shows in the right places.</p>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-2">
            <input placeholder="Category name (e.g. FANCY BALI)" className="border border-line px-3 py-2 bg-pearl text-sm" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="cat-name"/>
            <input placeholder="Item family (e.g. BALI)" className="border border-line px-3 py-2 bg-pearl text-sm" value={form.item_name} onChange={(e) => setForm({ ...form, item_name: e.target.value })}/>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <input placeholder="Comes under (e.g. Earrings, Rings, Bangles)" className="border border-line px-3 py-2 bg-pearl text-sm" value={form.parent_category} onChange={(e) => setForm({ ...form, parent_category: e.target.value })} data-testid="cat-parent"/>
            <select className="border border-line px-3 py-2 bg-pearl text-sm" value={form.metal || "Gold"} onChange={(e) => setForm({ ...form, metal: e.target.value })} data-testid="cat-metal">
              <option value="Gold">Gold (HUID ₹1250 applies)</option>
              <option value="Silver">Silver (no HUID)</option>
              <option value="Gemstone">Gemstone (no HUID)</option>
            </select>
          </div>
          <div>
            <div className="text-[10px] text-ash uppercase tracking-widest mb-1">Occasions</div>
            <div className="flex flex-wrap gap-2">
              {OCCASIONS.map((o) => (
                <button key={o} onClick={() => toggleOcc(o)} className={`text-xs px-2.5 py-1 rounded-full border ${form.occasions.includes(o) ? "bg-maroon text-pearl border-maroon" : "border-line"}`} data-testid={`cat-occ-${o}`}>{o}</button>
              ))}
            </div>
          </div>
          <input placeholder="Gender (optional)" className="w-full border border-line px-3 py-2 bg-pearl text-sm" value={form.gender} onChange={(e) => setForm({ ...form, gender: e.target.value })}/>
          <textarea placeholder="Description (optional)" rows={2} className="w-full border border-line px-3 py-2 bg-pearl text-sm" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}/>
          <div>
            <div className="text-[10px] text-ash uppercase tracking-widest mb-1">Images</div>
            <ImageUploader value={form.images} onChange={(imgs) => setForm({ ...form, images: imgs })} multiple kind="inventory" maxFiles={5}/>
          </div>
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.made_to_order} onChange={(e) => setForm({ ...form, made_to_order: e.target.checked })}/> Made to order (no stock limit — never shows "Sold Out")</label>

          {/* How the uploaded pieces of this design show up on the website. */}
          <div className="border border-line bg-pearl/60 p-3">
            <label className="flex items-start gap-2 text-sm">
              <input
                type="checkbox"
                className="mt-1"
                checked={form.same_image_for_all !== false}
                onChange={(e) => setForm({ ...form, same_image_for_all: e.target.checked })}
                data-testid="cat-same-image"
              />
              <span>
                <b>Same images for all sizes / weights</b>
                <div className="text-[11px] text-ash mt-1">
                  {form.same_image_for_all !== false ? (
                    <>
                      <b className="text-maroon">On.</b> One product page for this design, with each
                      stocked weight offered as a choice on it — "10 g", "12.5 g", "13 g" — each showing
                      its own price and how many of that weight are left. Use this when every piece
                      looks the same and only the weight differs.
                    </>
                  ) : (
                    <>
                      <b className="text-maroon">Off.</b> Every uploaded piece becomes its own separate
                      product on the website — one per barcode, each with its own weight, price and a
                      stock of one. Use this when the pieces genuinely look different.
                    </>
                  )}
                </div>
              </span>
            </label>
          </div>
          <div className="flex gap-2">
            <button onClick={save} className="btn-maroon" data-testid="cat-save">{editing ? "Update" : "Add category"}</button>
            {editing && <button onClick={() => { setEditing(null); setForm(EMPTY_CAT); }} className="btn-outline text-sm">Cancel</button>}
          </div>
        </div>
      </div>

      <div className="space-y-2">
        {items.map((c) => (
          <div key={c.id} className="bg-white border border-line p-3 flex items-center gap-3" data-testid={`cat-row-${c.id}`}>
            {c.images?.[0] && <img src={c.images[0]} alt="" className="w-12 h-12 object-cover rounded"/>}
            <div className="flex-1 min-w-0">
              <div className="font-medium">{c.name} {c.made_to_order && <span className="text-[9px] uppercase bg-gold/20 px-1.5 py-0.5 rounded">MTO</span>}</div>
              <div className="text-[10px] text-ash">{c.item_name}{c.parent_category ? ` · under ${c.parent_category}` : ""} · {(c.occasions || []).join(", ") || "no occasions"}</div>
              <div className="text-[10px] mt-0.5">
                <span className={`px-1.5 py-0.5 rounded ${c.same_image_for_all !== false ? "bg-gold/20 text-maroon" : "bg-blue-50 text-blue-700"}`}>
                  {c.same_image_for_all !== false ? "One page · weight choices" : "One product per piece"}
                </span>
              </div>
            </div>
            <button onClick={() => edit(c)} className="text-xs text-maroon underline">Edit</button>
            <button onClick={() => del(c.id)} className="text-red-600"><X size={16}/></button>
          </div>
        ))}
        {items.length === 0 && <div className="text-sm text-ash">No categories yet.</div>}
      </div>
    </div>
  );
}

// ---------------- Upload ----------------
function UploadTab() {
  const fileRef = useRef(null);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [history, setHistory] = useState([]);
  const loadHistory = () => api.get("/api/crm/inventory/uploads").then((r) => setHistory(r.data || [])).catch(() => {});
  useEffect(() => { loadHistory(); }, []);

  const doUpload = async (commit) => {
    if (!file) { toast.error("Choose the label report file"); return; }
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post(`/api/crm/inventory/upload?commit=${commit}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPreview(data);
      if (commit) { toast.success(`Added ${data.added} new · ${data.already_present} already in · ${data.failed} failed`); loadHistory(); }
    } catch (e) { toast.error(e?.response?.data?.detail || "Upload failed"); }
    finally { setBusy(false); }
  };

  const downloadFailed = () => {
    if (!preview?.failed_rows?.length) return;
    const csv = "row,label_no,reason\n" + preview.failed_rows.map((r) => `${r.row},${r.label_no || ""},"${r.reason}"`).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = "failed-rows.csv"; a.click();
    setTimeout(() => URL.revokeObjectURL(a.href), 5000);
  };

  return (
    <div className="grid lg:grid-cols-2 gap-6">
      <div className="bg-white border border-line p-5">
        <div className="eyebrow mb-2">Upload label report</div>
        <p className="text-[11px] text-ash mb-3">Full-syncs per item family. Already-present labels are never re-added (upload the same file 10× → each piece stays once). Rows that can't be read are listed with the row number + reason so you can fix them and re-upload — only the newly-fixed rows get added.</p>
        <input ref={fileRef} type="file" accept=".xlsx,.xls,.csv" onChange={(e) => { setFile(e.target.files?.[0]); setPreview(null); }} className="text-sm mb-3 block" data-testid="inv-file"/>
        <div className="flex gap-2">
          <button onClick={() => doUpload(false)} disabled={busy || !file} className="btn-outline text-sm" data-testid="inv-preview">{busy ? "…" : "Preview (dry run)"}</button>
          <button onClick={() => doUpload(true)} disabled={busy || !file} className="btn-maroon" data-testid="inv-commit">Upload & sync</button>
        </div>

        {preview && (
          <div className="mt-4 border-t border-line pt-3 text-sm" data-testid="inv-preview-report">
            {preview.dry_run && <div className="text-[11px] uppercase tracking-widest text-amber-700 mb-2">Dry run — nothing saved yet</div>}
            <div className="grid grid-cols-4 gap-2 mb-2">
              <Stat label="Passed" value={preview.passed}/>
              <Stat label="New" value={preview.added}/>
              <Stat label="Already in" value={preview.already_present}/>
              <Stat label="Failed" value={preview.failed}/>
            </div>
            <div className="text-[11px] text-ash">Families: {preview.families?.join(", ")} · {preview.total_rows} rows · {preview.to_mark_sold} → sold</div>
            {preview.categories_created?.length > 0 && (
              <div className="mt-2 text-[11px] bg-gold/10 border border-gold/40 p-2">
                Created {preview.categories_created.length} new design{preview.categories_created.length > 1 ? "s" : ""} from this file: <b>{preview.categories_created.join(", ")}</b>. Add images and occasions to them under Categories.
              </div>
            )}
            {preview.warning && <div className="mt-2 text-xs bg-amber-50 border border-amber-200 text-amber-800 p-2 rounded">{preview.warning}</div>}
          </div>
        )}
      </div>

      <div>
        {/* Failed rows — what to fix and re-upload */}
        {preview?.failed_rows?.length > 0 && (
          <div className="bg-white border border-red-200 mb-4" data-testid="failed-rows">
            <div className="flex items-center justify-between p-3 border-b border-line bg-red-50">
              <div className="text-sm font-medium text-red-700">{preview.failed} rows need fixing</div>
              <button onClick={downloadFailed} className="text-xs text-maroon underline">Download CSV</button>
            </div>
            <Paginated items={preview.failed_rows} pageSize={10} label="rows">
              {(page) => (
                <table className="w-full text-xs">
                  <thead className="bg-pearl border-b border-line"><tr>{["Row", "Label", "Reason"].map(h => <th key={h} className="text-left p-2 eyebrow text-[10px]">{h}</th>)}</tr></thead>
                  <tbody>
                    {page.map((r, i) => (
                      <tr key={i} className="border-b border-line/60 bg-red-50/30" data-testid={`failed-${r.row}`}>
                        <td className="p-2 font-medium">{r.row}</td>
                        <td className="p-2 font-mono">{r.label_no || "—"}</td>
                        <td className="p-2 text-red-700">{r.reason}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </Paginated>
          </div>
        )}

        <div className="eyebrow mb-2">Recent uploads</div>
        <div className="space-y-2">
          {history.map((u) => (
            <div key={u.id} className="bg-white border border-line p-2.5 text-xs" data-testid={`upload-${u.id}`}>
              <div className="flex justify-between"><span className="font-medium">{u.filename}</span><span className="text-ash">{new Date(u.created_at).toLocaleString()}</span></div>
              <div className="text-ash">by {u.by} · +{u.added} new · {u.already_present ?? u.kept ?? 0} already · {u.failed ?? 0} failed · {u.sold} sold · {(u.families || []).join(", ")}</div>
            </div>
          ))}
          {history.length === 0 && <div className="text-sm text-ash">No uploads yet.</div>}
        </div>
      </div>
    </div>
  );
}
const Stat = ({ label, value }) => (
  <div className="bg-pearl border border-line p-2 text-center"><div className="font-display text-2xl text-maroon">{value}</div><div className="text-[9px] uppercase tracking-widest text-ash">{label}</div></div>
);

// ---------------- Store credit approvals ----------------
function CreditsTab() {
  const [rows, setRows] = useState([]);
  const [status, setStatus] = useState("pending");
  const load = () => api.get(`/api/crm/inventory/credits?status=${status}`).then((r) => setRows(r.data || [])).catch(() => {});
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [status]);
  const act = async (id, action) => { try { await api.post(`/api/crm/inventory/credits/${id}/${action}`); toast.success(action === "approve" ? "Approved" : "Rejected"); load(); } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } };
  return (
    <div>
      <div className="flex gap-2 mb-4">
        {["pending", "approved", "rejected"].map((s) => (
          <button key={s} onClick={() => setStatus(s)} className={`text-xs px-3 py-1.5 rounded-full border capitalize ${status === s ? "bg-maroon text-pearl border-maroon" : "border-line"}`}>{s}</button>
        ))}
      </div>
      <div className="bg-white border border-line overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-pearl border-b border-line"><tr>{["Customer", "Amount", "Remaining", "Reason", "Raised by", "Status", ""].map(h => <th key={h} className="text-left p-3 eyebrow text-[10px]">{h}</th>)}</tr></thead>
          <tbody>
            {rows.map((c) => (
              <tr key={c.id} className="border-b border-line/60" data-testid={`credit-${c.id}`}>
                <td className="p-3">{c.customer_name || "—"}<div className="text-[10px] text-ash">{c.customer_phone}</div></td>
                <td className="p-3 font-semibold text-maroon">{fmtINR(c.amount)}</td>
                <td className="p-3 text-xs">{fmtINR(c.remaining)}</td>
                <td className="p-3 text-xs text-ash">{c.reason || "—"}{c.label_no ? ` · ${c.label_no}` : ""}</td>
                <td className="p-3 text-xs">{c.raised_by}</td>
                <td className="p-3"><span className="text-[10px] uppercase tracking-widest">{c.status}{c.approved_by ? ` · ${c.approved_by}` : ""}</span></td>
                <td className="p-3">
                  {c.status === "pending" && (
                    <div className="flex gap-2">
                      <button onClick={() => act(c.id, "approve")} className="btn-maroon text-xs" data-testid={`approve-credit-${c.id}`}>Approve</button>
                      <button onClick={() => act(c.id, "reject")} className="text-xs text-red-600 underline">Reject</button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {rows.length === 0 && <div className="p-12 text-center text-ash text-sm">No {status} credit requests.</div>}
      </div>
    </div>
  );
}
